'use client'

import BlogPostVM from 'clients/strapi/models/BlogPostVM'
import Pill from 'components/Pill/Pill'
import paths from 'constants/paths'
import { motion } from 'motion/react'
import Link from 'next/link'
import styles from './BlogThumbnail.module.scss'

const MotionLink = motion.create(Link)

type BlogThumbnailProps = {
  post: BlogPostVM
  size?: 'big' | 'small'
  recommended?: boolean
  onClick?: () => void
}

const BlogThumbnail = ({ post, size, recommended = false, onClick }: BlogThumbnailProps) => {
  const photoVariants = {
    start: {
      opacity: 0,
      translateY: 30,
      translateX: -30,
      zIndex: 0,
    },
    hover: {
      opacity: 1,
      translateY: 0,
      translateX: 0,
    },
  }

  const variants = {
    start: {
      scale: 1,
      opacity: 0,
      translateY: 100,
    },
    hover: {
      scale: 1.03,
    },
    enter: {
      opacity: 1,
      translateY: 0,
    },
    exit: {
      opacity: 0,
      translateY: 100,
    },
  }

  // Only for same-tab navigation; modified clicks open a new tab and must not leave state behind in this one.
  function handleOnClick(e: React.MouseEvent) {
    if (onClick && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
      onClick()
    }
  }

  return (
    <MotionLink
      href={paths.blogPost(post.slug)}
      prefetch={false}
      layout='position'
      initial='start'
      whileHover='hover'
      animate='enter'
      exit='exit'
      variants={variants}
      className={styles.thumbnail + ' ' + ((post.featured ? styles[size!] : '') + ' ' + (recommended ? styles.recommended : ''))}
      onClick={handleOnClick}
    >
      <div className={styles.cover} style={{ backgroundImage: `url(${post.cover.url})` }}>
        <motion.div variants={photoVariants} className={styles['author-photo']} style={{ backgroundImage: `url(${post.author.avatar?.url})` }}></motion.div>
      </div>
      <div className={styles.info}>
        <span className={styles.title} title={post.title}>
          {post.title}
        </span>
        <span className={styles.subtitle}>{post.description}</span>
      </div>
      <div className={styles.details}>
        <div className={styles.author}>
          <span className={styles.name}>by {post.author.name}</span>
          <span className={styles.time}>{post.readingTimeMinutes} min read</span>
        </div>
        <Pill className={styles.category}>{post.category.name}</Pill>
      </div>
    </MotionLink>
  )
}

export default BlogThumbnail
